import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner, Schedule, Task } from '@medplum/fhirtypes';
import {
  EXT_URLS,
  PROVIDER_DOCUMENT_TYPE_SYSTEM,
  PROVIDER_ONBOARDING_PARENT_TASK_CODE,
  PROVIDER_ONBOARDING_SECTION_TASK_CODES,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { hasOnboardingAttestation } from '@unify/fhir-model/resources/practitioner';
import { PROVIDER_ROLES, type Role } from '../../pages/users/types/rbac';
import {
  evaluateProviderOnboardingSections,
  getDefaultProviderOnboardingSectionResults,
  type ProviderOnboardingSectionResults,
} from './providerOnboardingRequirements';
import { ONBOARDING_SECTION_IDS, REQUIRED_SECTIONS, type OnboardingSectionId } from './onboardingTypes';
import { useOnboardingPractitioner } from './useOnboardingPractitioner';

export type OnboardingStatus = 'not_started' | 'in_progress' | 'completed';

export interface OnboardingStatusResult {
  status: OnboardingStatus | null;
  isLoading: boolean;
  sections: ProviderOnboardingSectionResults;
  unlockedSections: Record<OnboardingSectionId, boolean>;
  completedCount: number;
  progressPercent: number;
  allRequiredSectionsComplete: boolean;
}

function getRoleCode(resource: Practitioner): string | undefined {
  const extensions = (resource as { extension?: Array<{ url: string; valueCode?: string; valueString?: string }> }).extension;
  return extensions?.find((extension) => extension.url === EXT_URLS.role)?.valueCode
    ?? extensions?.find((extension) => extension.url === EXT_URLS.role)?.valueString;
}

function getDefaultUnlockedSections(): Record<OnboardingSectionId, boolean> {
  return {
    'personal-info': true,
    availability: true,
    profile: true,
    'licenses-education': true,
    attestation: false,
  };
}

function taskPrimaryCode(task: Task): string | undefined {
  return task.code?.coding?.find((c) => c.system && c.code)?.code;
}

function buildTaskCompletionBySection(children: Task[]): Record<OnboardingSectionId, boolean> {
  const out: Record<OnboardingSectionId, boolean> = {
    'personal-info': false,
    availability: false,
    profile: false,
    'licenses-education': false,
    attestation: false,
  };
  for (const task of children) {
    const code = taskPrimaryCode(task);
    if (!code || task.status !== 'completed') {
      continue;
    }
    const sectionEntry = (Object.entries(PROVIDER_ONBOARDING_SECTION_TASK_CODES) as Array<
      [OnboardingSectionId, string]
    >).find(([, v]) => v === code);
    if (sectionEntry) {
      out[sectionEntry[0]] = true;
    }
  }
  return out;
}

function buildUnlockedSectionsFromTasks(sections: ProviderOnboardingSectionResults): Record<OnboardingSectionId, boolean> {
  return {
    'personal-info': true,
    availability: true,
    profile: true,
    'licenses-education': true,
    attestation:
      sections['personal-info'].complete &&
      sections.availability.complete &&
      sections.profile.complete &&
      sections['licenses-education'].complete,
  };
}

function countCompletedSections(results: ProviderOnboardingSectionResults): number {
  return REQUIRED_SECTIONS.filter((sectionId) => results[sectionId].complete).length;
}

function buildResult(
  status: OnboardingStatus | null,
  sections: ProviderOnboardingSectionResults,
  isLoading: boolean,
): OnboardingStatusResult {
  const completedCount = countCompletedSections(sections);
  const total = ONBOARDING_SECTION_IDS.length;

  return {
    status,
    isLoading,
    sections,
    unlockedSections: buildUnlockedSectionsFromTasks(sections),
    completedCount,
    progressPercent: Math.round((completedCount / total) * 100),
    allRequiredSectionsComplete: REQUIRED_SECTIONS.every((sectionId) => sections[sectionId].complete),
  };
}

const DEFAULT_SECTION_RESULTS = getDefaultProviderOnboardingSectionResults();
const DEFAULT_RESULT = buildResult(null, DEFAULT_SECTION_RESULTS, true);

const ALL_COMPLETE_SECTIONS: ProviderOnboardingSectionResults = {
  'personal-info': { complete: true, missingFields: [] },
  availability: { complete: true, missingFields: [] },
  profile: { complete: true, missingFields: [] },
  'licenses-education': { complete: true, missingFields: [] },
  attestation: { complete: true, missingFields: [] },
};

async function loadProviderSchedule(
  medplum: ReturnType<typeof useMedplum>,
  practitionerId: string,
): Promise<Schedule | null> {
  medplum.invalidateSearches('Schedule');
  const schedules = await medplum.searchResources('Schedule', {
    actor: `Practitioner/${practitionerId}`,
    _count: '1',
  });
  return schedules[0] ?? null;
}

/**
 * Derives onboarding status from FHIR onboarding Tasks when present, otherwise from
 * persisted Practitioner / DocumentReference / Schedule requirements (so gating still works for
 * older accounts or if Task search is misconfigured).
 *
 * Only applies to provider and supervisor roles. Other Practitioner-based
 * roles (practice-owner, practice-admin, etc.) are not subject to provider
 * onboarding and receive `null` so callers skip redirect logic.
 *
 * Already-attested practitioners (legacy short-circuit or completed parent Task) stay complete
 * without requiring a Schedule (grandfathered).
 */
export function useOnboardingStatus(): OnboardingStatusResult {
  const medplum = useMedplum();
  const location = useLocation();
  const sessionProfile = useMedplumProfile();
  const practitioner = useOnboardingPractitioner();
  const [result, setResult] = useState<OnboardingStatusResult>(DEFAULT_RESULT);
  const practitionerVersionKey = practitioner?.resourceType === 'Practitioner'
    ? `${practitioner.id ?? 'unknown'}:${practitioner.meta?.versionId ?? practitioner.meta?.lastUpdated ?? 'initial'}`
    : undefined;
  const sessionPractitionerVersionKey = sessionProfile?.resourceType === 'Practitioner'
    ? `${sessionProfile.id ?? 'unknown'}:${sessionProfile.meta?.versionId ?? sessionProfile.meta?.lastUpdated ?? 'initial'}`
    : undefined;

  useEffect(() => {
    const activePractitioner =
      practitioner?.resourceType === 'Practitioner'
        ? practitioner
        : sessionProfile?.resourceType === 'Practitioner'
          ? sessionProfile as Practitioner
          : undefined;

    if (!sessionProfile) {
      setResult(buildResult(null, DEFAULT_SECTION_RESULTS, false));
      return;
    }

    if (!activePractitioner) {
      setResult(buildResult(null, DEFAULT_SECTION_RESULTS, false));
      return;
    }

    const roleCode = getRoleCode(activePractitioner);
    if (!roleCode || !PROVIDER_ROLES.includes(roleCode as Role)) {
      setResult(buildResult(null, DEFAULT_SECTION_RESULTS, false));
      return;
    }

    if (!activePractitioner.id) {
      setResult(buildResult('not_started', DEFAULT_SECTION_RESULTS, false));
      return;
    }

    let cancelled = false;
    setResult((current) => ({ ...current, isLoading: true, unlockedSections: getDefaultUnlockedSections() }));

    const pid = activePractitioner.id;

    void (async (): Promise<void> => {
      try {
        // Grandfather already-attested accounts even if a parent Task still exists mid-flight.
        if (hasOnboardingAttestation(activePractitioner)) {
          setResult(buildResult('completed', ALL_COMPLETE_SECTIONS, false));
          return;
        }

        const parentTasks = await medplum.searchResources('Task', {
          owner: `Practitioner/${pid}`,
          code: `${TASK_TYPE_SYSTEM}|${PROVIDER_ONBOARDING_PARENT_TASK_CODE}`,
          _count: '1',
        });

        if (cancelled) {
          return;
        }

        const parent = parentTasks[0];
        if (!parent?.id) {
          const [driverLicenseDocumentsLegacy, schedule] = await Promise.all([
            medplum.searchResources('DocumentReference', {
              author: `Practitioner/${pid}`,
              type: `${PROVIDER_DOCUMENT_TYPE_SYSTEM}|drivers-license`,
              _count: '1',
            }),
            loadProviderSchedule(medplum, pid),
          ]);

          if (cancelled) {
            return;
          }

          const sectionsFromPractitioner = evaluateProviderOnboardingSections(activePractitioner, {
            driverLicenseDocument: driverLicenseDocumentsLegacy[0] ?? null,
            schedule,
          });
          const completedCountLegacy = countCompletedSections(sectionsFromPractitioner);
          const statusLegacy: OnboardingStatus = sectionsFromPractitioner.attestation.complete
            ? 'completed'
            : completedCountLegacy === 0
              ? 'not_started'
              : 'in_progress';

          setResult(buildResult(statusLegacy, sectionsFromPractitioner, false));
          return;
        }

        if (parent.status === 'completed') {
          setResult(buildResult('completed', ALL_COMPLETE_SECTIONS, false));
          return;
        }

        const [children, driverLicenseDocuments, schedule] = await Promise.all([
          medplum.searchResources('Task', {
            'part-of': `Task/${parent.id}`,
            _count: '20',
          }),
          medplum.searchResources('DocumentReference', {
            author: `Practitioner/${pid}`,
            type: `${PROVIDER_DOCUMENT_TYPE_SYSTEM}|drivers-license`,
            _count: '1',
          }),
          loadProviderSchedule(medplum, pid),
        ]);

        if (cancelled) {
          return;
        }

        const taskComplete = buildTaskCompletionBySection(children);
        const evaluated = evaluateProviderOnboardingSections(activePractitioner, {
          driverLicenseDocument: driverLicenseDocuments[0] ?? null,
          schedule,
        });

        // Availability stays complete only while the Schedule still satisfies requirements.
        const availabilityComplete = taskComplete.availability && evaluated.availability.complete;

        const sections: ProviderOnboardingSectionResults = {
          'personal-info': {
            complete: taskComplete['personal-info'],
            missingFields: taskComplete['personal-info'] ? [] : evaluated['personal-info'].missingFields,
          },
          availability: {
            complete: availabilityComplete,
            missingFields: availabilityComplete ? [] : evaluated.availability.missingFields,
          },
          profile: {
            complete: taskComplete.profile,
            missingFields: taskComplete.profile ? [] : evaluated.profile.missingFields,
          },
          'licenses-education': {
            complete: taskComplete['licenses-education'],
            missingFields: taskComplete['licenses-education'] ? [] : evaluated['licenses-education'].missingFields,
          },
          attestation: {
            complete: taskComplete.attestation,
            missingFields: taskComplete.attestation ? [] : evaluated.attestation.missingFields,
          },
        };

        const completedCount = countCompletedSections(sections);
        const onboardingDone =
          (['personal-info', 'availability', 'profile', 'licenses-education', 'attestation'] as const).every(
            (sid) => sections[sid].complete,
          );

        const status: OnboardingStatus = onboardingDone
          ? 'completed'
          : completedCount === 0
            ? 'not_started'
            : 'in_progress';

        setResult(buildResult(status, sections, false));
      } catch {
        if (!cancelled) {
          setResult(buildResult('not_started', DEFAULT_SECTION_RESULTS, false));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, practitionerVersionKey, sessionPractitionerVersionKey, sessionProfile?.resourceType, location.pathname]);

  return result;
}
